import React, { forwardRef, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Divider,
  Stack,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import request from "../../../utilities/requests";


// Slide-up transition
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// simple currency helper (USD default)
const formatMoney = (value, currency = "USD") =>
  (typeof value === "number" ? value : Number(value || 0)).toLocaleString(
    undefined,
    {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

const Mobile = ({
  eventId,
  ticketTypes = [],
  open,
  close,
  title = "Save your spot",
  startsAt,
  qty = {},
  incQty,
  decQty,
  promo,
  setPromo,
  cartTotals,
  cartLoading,
  cartExpiresAt,
  applyPromo,
  waitListEnabled = false
}) => {
  const navigate = useNavigate();
  const salesEnds = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const eventWhen = startsAt
    ? new Date(startsAt).toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  const hasItems = useMemo(() => {
    if (!qty) return false;
    return Object.values(qty).some((n) => Number(n) > 0);
  }, [qty]);

  const [remainingMs, setRemainingMs] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const [waitListOpen, setWaitListOpen] = useState(false);
  const [waitListSuccess, setWaitListSuccess] = useState(false);
  const [duplicate, setDuplicate] = useState(false);

  const [waitListForm, setWaitListForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    ticketTypeId: null,
  });

  const [waitListErrors, setWaitListErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
  });

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleOpenWaitList = (ticketTypeId) => {
    setWaitListSuccess(false);
    setDuplicate(false);
    setWaitListErrors({
      firstName: false,
      lastName: false,
      email: false,
    });
    setWaitListForm({
      firstName: "",
      lastName: "",
      email: "",
      ticketTypeId,
    });
    setWaitListOpen(true);
  };

  const handleSubmitWaitList = async () => {
    const errors = {
      firstName: !waitListForm.firstName.trim(),
      lastName: !waitListForm.lastName.trim(),
      email: !waitListForm.email.trim() || !isValidEmail(waitListForm.email),
    };

    setWaitListErrors(errors);

    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) return;

    const payload = {
      firstName: waitListForm.firstName.trim(),
      lastName: waitListForm.lastName.trim(),
      email: waitListForm.email.trim().toLowerCase(),
      ticketTypeId: waitListForm.ticketTypeId,
    };

    const result = await request('unauth/waitlist','post',payload);
    if (result.success) {
      setWaitListSuccess(true);
    } else if (!result.success && result.duplicate) {
      setDuplicate(true)
    } else {
      alert(result.error);
    }

    
  };

  const formatCountdown = (ms) => {
    if (ms == null) return null;
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  useEffect(() => {
    let timer;
    const update = () => {
      if (!cartExpiresAt) {
        setRemainingMs(null);
        return;
      }
      const diff = new Date(cartExpiresAt).getTime() - Date.now();
      setRemainingMs(diff > 0 ? diff : 0);
    };
    update();
    if (cartExpiresAt) {
      timer = setInterval(update, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cartExpiresAt]);

  useEffect(() => {
    console.log(cartTotals)
  },[cartTotals])

  return (
    <Dialog
      fullScreen
      open={!!open}
      onClose={close}
      TransitionComponent={Transition}
    >
      <AppBar
        elevation={0}
        color="transparent"
        position="static"
        sx={{ bgcolor: "#f3f4ef", color: "inherit" }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              pr: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          {eventWhen && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 2, display: { xs: "none", sm: "block" } }}
            >
              {eventWhen}
            </Typography>
          )}
          <IconButton edge="end" onClick={close} aria-label="Close checkout">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Promo code */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <Stack direction="row" spacing={1}>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              placeholder="Promo Code"
              value={promo.toUpperCase()}
              onChange={(e) => setPromo && setPromo(e.target.value.toUpperCase())}
              inputProps={{ "aria-label": "Promo code" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#cfcfcf",
                    borderWidth: "1px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#9e9e9e",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#666",
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <Button
              variant="outlined"
              onClick={() => applyPromo && applyPromo()}
              disabled={!applyPromo}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Ticket cards or loading */}
      <Box sx={{ p: 2, pt: 0, pb: 10, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 600 }}>
          {open && (!ticketTypes || ticketTypes.length === 0) ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : (
            (ticketTypes || []).map((t) => {
              const available = Math.max(0, Number(t.available || 0));
              const mpoNum = Number(t.maxPerOrder);
              const mpo = Number.isFinite(mpoNum) ? mpoNum : Infinity;
              const cap = Math.min(available, mpo);
              const current = qty[t.id] || 0;
              const incDisabled = current >= cap;
              const unitFees = Number(t.totalFees ?? 0);
              const tax = Number(t?.taxAmount ?? 0);
              const unitTotal = Number.isFinite(Number(t.totalCost))
                ? Number(t.totalCost)
                : Number(t.price || 0) + unitFees;

              return (
                <Paper
                  key={t.id}
                  variant="outlined"
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {t.name}
                    </Typography>
                    {cap <= 0 ? (
                      waitListEnabled ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenWaitList(t.id)}
                        >
                          Wait List
                        </Button>
                      ) : (
                        <Typography
                          variant="overline"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: ".08em",
                            color: "text.secondary",
                            textTransform: "uppercase",
                          }}
                        >
                          Sold out
                        </Typography>
                      )
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => decQty && decQty(t)}
                          aria-label={`Decrease ${t.name}`}
                          disabled={current <= 0}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography sx={{ minWidth: 16, textAlign: "center" }}>
                          {current}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => incQty && incQty(t)}
                          aria-label={`Increase ${t.name}`}
                          disabled={incDisabled}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>
                    )}
                  </Box>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {t.isFree ? "Free" : formatMoney(unitTotal, t.currency)}
                      {(unitFees > 0 || tax > 0) && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          {unitFees > 0 && `incl. ${formatMoney(unitFees, t.currency)} fee`}
                          {unitFees > 0 && tax > 0 && " + "}
                          {tax > 0 && `${formatMoney(tax, t.currency)} sales tax`}
                        </Typography>
                      )}
                    </Typography>

                    {t.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {t.description}
                      </Typography>
                    )}

                    {t.salesEndAt && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Sales end on {salesEnds(t.salesEndAt)}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>
      </Box>

      {/* Bottom summary bar */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "#f3f4ef",
          borderTop: 1,
          borderColor: "divider",
          zIndex: (t) => t.zIndex.appBar,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Stack spacing={0}>
              <Typography variant="subtitle1">
                {formatMoney(cartTotals?.total || 0)}
              </Typography>
              {(cartTotals?.fees > 0 || cartTotals?.tax > 0) && (
                <Typography variant="caption" color="text.secondary">
                  {cartTotals?.fees > 0 && `incl. ${formatMoney(cartTotals.fees)} fee`}
                  {cartTotals?.fees > 0 && cartTotals?.tax > 0 && " / "}
                  {cartTotals?.tax > 0 && `incl. ${formatMoney(cartTotals.tax)} sales tax`}
                </Typography>
              )}
            </Stack>
            <IconButton
              size="small"
              aria-label="View total breakdown"
              onClick={() => setBreakdownOpen(true)}
              sx={{ ml: 0.5, p: 0.25, verticalAlign: 'super', transform: 'translateY(-2px)' }}
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
            {cartLoading && <CircularProgress size={30} style={{marginLeft: 20}}/>}
            {!cartLoading && cartExpiresAt && (
              <Typography
                variant="body2"
                color={
                  remainingMs !== null && remainingMs <= 60000
                    ? "error"
                    : "text.secondary"
                }
                sx={{ ml: 1 }}
              >
                {remainingMs === 0
                  ? "Hold expired"
                  : `Cart expires in ${formatCountdown(remainingMs ?? 0)}`}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            disabled={cartLoading || !hasItems}
            onClick={() => {
              navigate(`/checkout/${eventId}`)
            }}
          >
            Check out
          </Button>
        </Box>
      </Box>

      {/* Compact breakdown dialog */}
      <Dialog
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="order-summary-title"
      >
        <DialogTitle id="order-summary-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Cost breakdown</Typography>
          <IconButton onClick={() => setBreakdownOpen(false)} aria-label="Close summary">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body1">Subtotal</Typography>
              <Typography variant="body1">{formatMoney(cartTotals?.subtotal || 0)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body1">Fees</Typography>
              <Typography variant="body1">{formatMoney(cartTotals?.fees || 0)}</Typography>
            </Stack>
            {cartTotals?.tax > 0 && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">Taxes</Typography>
                <Typography variant="body1">
                  {formatMoney(cartTotals?.tax || 0)}
                </Typography>
              </Stack>
            )}
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatMoney(cartTotals?.total || 0)}</Typography>
            </Stack>
          </Stack>
        </DialogContent>
       
      </Dialog>

      {/* Wait list dialog */}
      <Dialog
        open={waitListOpen}
        onClose={() => setWaitListOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Join Wait List</DialogTitle>
        <DialogContent>
          {waitListSuccess ? (
            <Typography sx={{ mt: 1 }}>
              You have been added to the waitlist. If/when a ticket becomes available, you will receive an email to purchase the ticket.
            </Typography>
          ) : duplicate ? (
            <Typography sx={{ mt: 1 }}>
              You are already on the waitlist for this ticket type. If/when a ticket becomes available, you will receive an email to purchase the ticket.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="First Name"
                fullWidth
                required
                error={waitListErrors.firstName}
                helperText={waitListErrors.firstName ? "First name is required" : ""}
                value={waitListForm.firstName}
                onChange={(e) => {
                  setWaitListForm((prev) => ({ ...prev, firstName: e.target.value }));
                  setWaitListErrors((prev) => ({ ...prev, firstName: false }));
                }}
              />
              <TextField
                label="Last Name"
                fullWidth
                required
                error={waitListErrors.lastName}
                helperText={waitListErrors.lastName ? "Last name is required" : ""}
                value={waitListForm.lastName}
                onChange={(e) => {
                  setWaitListForm((prev) => ({ ...prev, lastName: e.target.value }));
                  setWaitListErrors((prev) => ({ ...prev, lastName: false }));
                }}
              />
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                error={waitListErrors.email}
                helperText={waitListErrors.email ? "Please enter a valid email address" : ""}
                value={waitListForm.email}
                onChange={(e) => {
                  setWaitListForm((prev) => ({ ...prev, email: e.target.value }));
                  setWaitListErrors((prev) => ({ ...prev, email: false }));
                }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaitListOpen(false)}>
            {waitListSuccess || duplicate ? "Close" : "Cancel"}
          </Button>
          {!waitListSuccess && !duplicate && (
            <Button
              variant="contained"
              onClick={handleSubmitWaitList}
              disabled={
                !waitListForm.firstName.trim() ||
                !waitListForm.lastName.trim() ||
                !waitListForm.email.trim()
              }
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default Mobile;
